import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import '../Assets/Stylesheets/Components/MarketingForecast.scss';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function MarketingForecast({ forecastData }) {
  if (!forecastData) return null;

  const { roi, revenue, leads, cac, assumptions, summary } = forecastData;

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#FE9700',
        bodyColor: '#fff',
        borderColor: 'rgba(254, 151, 0, 0.3)',
        borderWidth: 1,
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11
          }
        }
      }
    }
  };

  // ROI Chart Data
  const roiChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'ROI (%)',
        data: roi,
        borderColor: '#FE9700',
        backgroundColor: 'rgba(254, 151, 0, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#FE9700',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  // Revenue Chart Data
  const revenueChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'Revenue ($)',
        data: revenue,
        borderColor: '#FFAA01',
        backgroundColor: 'rgba(255, 170, 1, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#FFAA01',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  // Leads Chart Data
  const leadsChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'Leads',
        data: leads,
        backgroundColor: 'rgba(254, 151, 0, 0.8)',
        borderColor: '#FE9700',
        borderWidth: 1,
      }
    ]
  };

  // CAC Chart Data
  const cacChartData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'CAC ($)',
        data: cac,
        borderColor: '#FF6300',
        backgroundColor: 'rgba(255, 99, 0, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#FF6300',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="forecast-container">
      <div className="forecast-header">
        <h3 className="forecast-title">📈 1-Year Marketing Forecast</h3>
        {summary && <p className="forecast-summary">{summary}</p>}
      </div>

      {/* Charts Grid */}
      <div className="forecast-charts-grid">
        {/* ROI Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Return on Investment (ROI)</h4>
            <div className="chart-stat">
              <span className="stat-label">Year End:</span>
              <span className="stat-value stat-roi">{roi[11]}%</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Line data={roiChartData} options={chartOptions} />
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Revenue Projection</h4>
            <div className="chart-stat">
              <span className="stat-label">Year End:</span>
              <span className="stat-value stat-revenue">{formatCurrency(revenue[11])}</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Line data={revenueChartData} options={chartOptions} />
          </div>
        </div>

        {/* Leads Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Lead Generation</h4>
            <div className="chart-stat">
              <span className="stat-label">Total Year:</span>
              <span className="stat-value stat-leads">{formatNumber(leads.reduce((a, b) => a + b, 0))}</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Bar data={leadsChartData} options={chartOptions} />
          </div>
        </div>

        {/* CAC Chart */}
        <div className="forecast-chart-card">
          <div className="chart-header">
            <h4 className="chart-title">Customer Acquisition Cost (CAC)</h4>
            <div className="chart-stat">
              <span className="stat-label">Year End:</span>
              <span className="stat-value stat-cac">{formatCurrency(cac[11])}</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Line data={cacChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Assumptions Section */}
      <div className="forecast-assumptions">
        <h4 className="assumptions-title">⚠️ Key Assumptions</h4>
        <p className="assumptions-disclaimer">
          This forecast is 100% dependent on the following assumptions. Actual results may vary.
        </p>
        <ul className="assumptions-list">
          {assumptions.map((assumption, index) => (
            <li key={index} className="assumption-item">
              <span className="assumption-bullet">{index + 1}</span>
              <span className="assumption-text">{assumption}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

